import * as Sentry from '@sentry/vue';
import { AssistantService } from '@/api/services/assistant.service';
import {
  OPTION_GROUPS_CHANGED_EVENT,
  STOCK_CHANGED_EVENT,
} from '@/domain/catalog/catalog-events';
import { getServerErrorStatus } from '@/domain/reservations/reservation-errors';
import { ASSISTANT_SCHEMA_VERSION } from '@/interfaces/assistant.interface';
import type {
  IAssistantApplyResult,
  IAssistantDiscardResponse,
  IAssistantProposalView,
  IAssistantRow,
  IAssistantStaleDetails,
  IAssistantSummary,
  IAssistantUndoResult,
  TAssistantInvalidOpReason,
  TAssistantProposalStatus,
} from '@/interfaces/assistant.interface';
import { EventBus } from '@/modules/EventBus';
import type { IGhCardActionResult } from '../gpthelp-host-contract';
import { clampText, fitText } from '../cards/card-text';
import {
  expiredProposalCard,
  hasRowsToApply,
  payloadKind,
  payloadRequiredKeys,
  proposalCard,
  proposalPayload,
  requiredRowLabel,
} from '../cards/proposal-cards';
import {
  needsRowKey,
  needsRowKeyOf,
  needsSizeRow,
  needsStockUnitRow,
  unmetRequirement,
} from '../cards/row-requirements';
import {
  appliedCard,
  appliedChangesOptionGroups,
  appliedStockChange,
  discardedCard,
  expiredNoticeCard,
  nothingLeftCard,
  staleCard,
  supersededCard,
  undoneCard,
} from '../cards/result-cards';
import { SIZE_CODES } from '../cards/result-titles';
import { changedGroupId, isSizeRow } from '../cards/row-presenters';
import { addsOtherThanProducts, addsProducts } from '../cards/row-values';
import { buildScopeKey } from '../tools/tool-scope';
import type { IActionRun } from './assistant-action-run';
import {
  NOTICE_MAX,
  refusalResult,
  requirePayloadString,
} from './assistant-action-run';
import { refreshAssistantConfigOnRefusal } from './assistant-config';
import { readAssistantError } from './assistant-errors';
import {
  discardEmptyProposal,
  proposalCardInput,
  stillHiddenByRow,
} from './assistant-proposal-context';
import { menuPageKey, stockPageKey } from './assistant-refusals';

/**
 * The write actions of a proposal's cards (plan §9.9): apply, discard and
 * refresh. Plain functions returning the card that replaces the tapped one
 * (it keeps its id) or a notice; no component state changes after an await.
 */

/**
 * Summaries whose receipt title names the subject of a single applied row
 * (a product; a category, plan P2-4).
 */
const NAMED_SINGLE_CODES = [
  'setPrice',
  'setDiscount',
  'renameItem',
  'setDescription',
  'setCategoryAvailability',
];

/**
 * `details.reason` of an apply whose ticked rows would break an option group
 * (plan P3-3).
 */
const GROUP_BREAKS_REASONS: readonly TAssistantInvalidOpReason[] = [
  'NO_ACTIVE_CHOICE',
  'RULES_INVALID',
];

/**
 * `details.reason` of an apply whose ticked rows would break a product's
 * variants (plan P4-1: the writer checks each product's approved size rows
 * before any write).
 */
const SIZE_BREAKS_REASONS: readonly TAssistantInvalidOpReason[] = [
  'NO_ACTIVE_SIZE',
  'SIZE_LIMIT',
  'SIZES_CONFLICT',
];

/** What is known about a proposal that is no longer waiting. */
interface IClosedProposal {
  /** Its current view, when it was read. */
  view?: IAssistantProposalView | null;
  /** 409 `ASSISTANT_NOT_PENDING` of an undone action. */
  undoResult?: IAssistantUndoResult;
}

/** The proposal as the API has it now; null when it cannot be read. */
async function readProposal(
  run: IActionRun,
  proposalId: string
): Promise<IAssistantProposalView | null> {
  try {
    return await AssistantService.getProposal(
      run.scope.restaurantId,
      proposalId
    );
  } catch (error) {
    console.warn('[assistant] proposal unavailable', error);
    return null;
  }
}

/** «No se pudo aplicar. Intente de nuevo.» under the card, which stays. */
function applyFailed(run: IActionRun): IGhCardActionResult {
  return {
    notice: clampText(run.fmt.t('assistant.result.applyFailed'), NOTICE_MAX),
  };
}

/**
 * A proposal still waiting but without rows (every product already changed
 * or is gone): nothing is left to apply, so it stops waiting and is
 * discarded, and the card says why.
 */
async function nothingLeftResult(
  run: IActionRun,
  proposalId: string
): Promise<IGhCardActionResult> {
  run.host.deps.assistantStore.clearPending(proposalId);
  await discardEmptyProposal(run.scope.restaurantId, proposalId);
  return {
    card: nothingLeftCard(
      run.event.cardId,
      run.payload.speaksOfChanges === true,
      run.fmt,
      payloadKind(run.payload)
    ),
  };
}

/**
 * A failure after which the apply may or may not have happened: no answer
 * (network error, timeout) or a server error without an assistant code.
 */
function isUncertain(error: unknown): boolean {
  const status = getServerErrorStatus(error);
  return status === null || status >= 500;
}

/** The rows the card showed (`payload.rows`); null on an older card. */
function payloadRows(run: IActionRun): number | null {
  const rows = run.payload.rows;
  return typeof rows === 'number' ? rows : null;
}

/**
 * Whether the applied rows may have turned on a product or a category: the
 * receipt then says what still hides it (plan P0-3, P2-4), which only its
 * row tells.
 */
function mayTurnOn(summary: IAssistantSummary): boolean {
  if (
    summary.code === 'setAvailability' ||
    summary.code === 'setCategoryAvailability'
  )
    return summary.available !== false;
  return summary.code === 'mixed';
}

/**
 * The receipt names or counts products and categories, which only the
 * proposal's rows tell: they are read after the apply when rows were left
 * out, when one row that the title names was applied, when the title may
 * name the product whose sizes changed (plan P4-1: a change of sizes, or a
 * mixed one, which may be several size ops on one product), or when a row
 * may have turned on something still hidden.
 */
function needsRows(
  result: IAssistantApplyResult,
  rowCount: number | null
): boolean {
  const keptSome = rowCount !== null && rowCount > result.appliedCount;
  const namedSingle =
    result.appliedCount === 1 &&
    NAMED_SINGLE_CODES.includes(result.summary.code);
  const namedProduct =
    result.summary.code === 'mixed' || SIZE_CODES.includes(result.summary.code);
  return keptSome || namedSingle || namedProduct || mayTurnOn(result.summary);
}

/** The diff card of `view` in place of the tapped card (it keeps its id). */
function diffCardResult(
  run: IActionRun,
  view: IAssistantProposalView
): IGhCardActionResult {
  return {
    card: {
      ...proposalCard(
        proposalCardInput(run.host.deps, view, view.requestText),
        run.fmt
      ),
      id: run.event.cardId,
    },
  };
}

/** The proposal's branch: its view's, or the one its card's payload names. */
function proposalBranchId(
  run: IActionRun,
  view: IAssistantProposalView | null
): number | null {
  if (view) return view.branchId;
  const { branchId } = run.payload;
  return typeof branchId === 'number' ? branchId : null;
}

/**
 * The receipt; the proposal no longer waits, and the model reads it as
 * recent. After a change to option groups themselves, the pages that list
 * them reload: a group no live product uses changes without a realtime
 * catalogUpdate. After an inventory change, the pages that show the
 * inventory follow it (plan P5-3: stock writes send no catalogUpdate).
 */
function appliedResult(
  run: IActionRun,
  result: IAssistantApplyResult,
  view: IAssistantProposalView | null
): IGhCardActionResult {
  const { host, scope } = run;
  // What still hides what it turned on, with the categories as the applied
  // rows alone leave them (a category row left unticked changed nothing).
  const applied = new Set(result.appliedKeys);
  const appliedRows = view?.rows.filter((row) => applied.has(row.key)) ?? [];
  // The apply result names no kind: the proposal does, or its card.
  const kind = view?.kind ?? payloadKind(run.payload);
  const branchId = proposalBranchId(run, view);
  const card = appliedCard(
    {
      id: run.event.cardId,
      result,
      rows: view?.rows ?? null,
      rowCount: view?.rowCount ?? payloadRows(run) ?? result.appliedCount,
      stillHidden: stillHiddenByRow(host.deps, appliedRows),
      restaurantId: scope.restaurantId,
      storeName: host.deps.restaurantStore.restaurant?.name ?? '',
      menuPage: menuPageKey(run.gate),
      kind,
      stockPage: stockPageKey(run.gate),
      branchId,
    },
    run.fmt
  );
  host.deps.assistantStore.clearPending(result.proposalId);
  host.deps.assistantStore.addRecent({
    actionId: result.proposalId,
    status: 'applied',
    title: card.title,
    at: result.appliedAt,
    scopeKey: buildScopeKey(scope),
  });
  if (appliedChangesOptionGroups(result, appliedRows))
    EventBus.$emit(OPTION_GROUPS_CHANGED_EVENT, scope.restaurantId);
  const stockChange = appliedStockChange(result, {
    kind,
    appliedRows: view ? appliedRows : null,
    restaurantId: scope.restaurantId,
    branchId,
  });
  if (stockChange) EventBus.$emit(STOCK_CHANGED_EVENT, stockChange);
  Sentry.addBreadcrumb({
    category: 'assistant',
    message: 'apply',
    data: {
      proposalId: result.proposalId,
      applied: result.appliedCount,
      replayed: result.replayed === true,
    },
  });
  return { card };
}

/**
 * 410: the proposal expired. The same diff card says so and offers to
 * prepare it again; a short notice card when it cannot be read.
 */
async function expiredResult(
  run: IActionRun,
  proposalId: string,
  known: IAssistantProposalView | null
): Promise<IGhCardActionResult> {
  run.host.deps.assistantStore.clearPending(proposalId);
  const view = known ?? (await readProposal(run, proposalId));
  if (view && hasRowsToApply(view))
    return {
      card: {
        ...expiredProposalCard(
          proposalCardInput(run.host.deps, view, view.requestText),
          run.fmt
        ),
        id: run.event.cardId,
      },
    };
  return { card: expiredNoticeCard(run.event.cardId, run.payload, run.fmt) };
}

/**
 * 409 `ASSISTANT_STALE`: nothing was applied. What changed, and the fresh
 * proposal (+30 minutes) to review again. The API saved that fresh proposal
 * with the changed rows marked (`payload.stale`): «Revisar de nuevo» shows
 * it as saved. With no rows left there is nothing to apply: it is discarded
 * and the card offers nothing. A fresh proposal that no longer waits
 * (applied from another tab, discarded, replaced or expired meanwhile)
 * answers its own card.
 */
async function staleResult(
  run: IActionRun,
  details: IAssistantStaleDetails | undefined
): Promise<IGhCardActionResult> {
  const fresh = details?.proposal ?? null;
  if (fresh && fresh.status !== 'proposed')
    return closedProposalResult(run, fresh.id, fresh.status, { view: fresh });
  const store = run.host.deps.assistantStore;
  const changed = details?.changed ?? [];
  const kind = fresh?.kind ?? payloadKind(run.payload);
  if (fresh && !hasRowsToApply(fresh)) {
    store.clearPending(fresh.id);
    await discardEmptyProposal(run.scope.restaurantId, fresh.id);
    return {
      card: staleCard(
        {
          id: run.event.cardId,
          changed,
          rows: fresh.rows,
          affected: fresh.affected,
          payload: null,
          kind,
        },
        run.fmt
      ),
    };
  }
  if (fresh)
    store.markPending(
      fresh.id,
      fresh.rowCount,
      fresh.expiresAt,
      null,
      buildScopeKey(run.scope)
    );
  return {
    card: staleCard(
      {
        id: run.event.cardId,
        changed,
        rows: fresh?.rows ?? [],
        affected: fresh?.affected ?? [],
        payload: fresh
          ? { ...proposalPayload(fresh), stale: true }
          : run.payload,
        kind,
      },
      run.fmt
    ),
  };
}

/**
 * The card of a proposal that no longer waits (409 `ASSISTANT_NOT_PENDING`,
 * or read after an uncertain apply): the receipt, undone, discarded,
 * superseded or expired.
 */
async function closedProposalResult(
  run: IActionRun,
  proposalId: string,
  status: TAssistantProposalStatus,
  known: IClosedProposal
): Promise<IGhCardActionResult> {
  const { event, fmt } = run;
  const store = run.host.deps.assistantStore;
  switch (status) {
    case 'applied': {
      const view = known.view ?? (await readProposal(run, proposalId));
      if (!view?.result)
        throw new Error('The applied proposal could not be read.');
      return appliedResult(run, view.result, view);
    }
    case 'undone':
      store.clearPending(proposalId);
      return {
        card: undoneCard(
          {
            id: event.cardId,
            kept: known.undoResult?.kept ?? 0,
            keptKeys: known.undoResult?.keptRows.map((row) => row.key),
            // A 409 names only the status: the card's payload knows the kind.
            created: known.view
              ? addsProducts(known.view.rows)
              : run.payload.created === true,
            createdAny: known.view
              ? addsOtherThanProducts(known.view.rows)
              : run.payload.createdAny === true,
            conversationAppliedCount:
              known.undoResult?.conversationAppliedCount,
          },
          fmt
        ),
      };
    case 'discarded':
      store.clearPending(proposalId);
      return { card: discardedCard(event.cardId, fmt) };
    case 'superseded':
      store.clearPending(proposalId);
      return { card: supersededCard(event.cardId, fmt) };
    case 'expired':
      return expiredResult(run, proposalId, known.view ?? null);
    default:
      throw new Error(`The proposal is still ${status}.`);
  }
}

/** The Action API refusals shared by apply, discard and refresh. */
async function proposalRefused(
  error: unknown,
  run: IActionRun,
  proposalId: string
): Promise<IGhCardActionResult> {
  const body = readAssistantError(error);
  switch (body?.code) {
    case 'ASSISTANT_NOT_PENDING':
      if (!body.details) throw error;
      return closedProposalResult(run, proposalId, body.details.status, {
        undoResult: body.details.undoResult,
      });
    case 'ASSISTANT_SUPERSEDED':
      run.host.deps.assistantStore.clearPending(proposalId);
      return { card: supersededCard(run.event.cardId, run.fmt) };
    case 'ASSISTANT_EXPIRED':
      return expiredResult(run, proposalId, null);
    default: {
      refreshAssistantConfigOnRefusal(body, run.host.deps);
      const refusal = refusalResult(error, run);
      if (!refusal) throw error;
      // Its card is gone: nothing is left to approve.
      run.host.deps.assistantStore.clearPending(proposalId);
      return refusal;
    }
  }
}

/** The needs-row notice; the card stays for the member to tick that row too. */
function needsRowResult(run: IActionRun, key: string): IGhCardActionResult {
  return { notice: clampText(run.fmt.t(key), NOTICE_MAX) };
}

/**
 * How the needs-row notice of a ticked row that needs `required` names it
 * (`{name}`): a size's removal or rename as its row is («Fugazzeta · Chica»,
 * plan P4-1: the product has other sizes), a stock item's unit change by
 * that stock item (plan P5-3, W20 decision U1). Null for a notice that
 * names no row.
 */
function requiredRowName(
  run: IActionRun,
  required: IAssistantRow
): string | null {
  if (isSizeRow(required)) return requiredRowLabel(required, run.fmt);
  return needsStockUnitRow(required.key) ? required.itemName : null;
}

/**
 * The needs-row notice of a ticked row that needs `required` (plan F2.6),
 * naming that row as `requiredRowName` does, with only the name shortened to
 * the notice cap. A row its notice does not name, or without a name to show,
 * gets its key family's notice, naming none (the Action API's NEEDS_ROW
 * gets the same). Null for a row without one.
 */
function requiredRowResult(
  run: IActionRun,
  required: IAssistantRow
): IGhCardActionResult | null {
  const key = needsRowKey(required);
  if (!key) return null;
  const name = requiredRowName(run, required)?.trim();
  if (name)
    return { notice: fitText(run.fmt, key, { name }, 'name', NOTICE_MAX) };
  return needsRowResult(run, needsRowKeyOf(required.key) ?? key);
}

/**
 * 422 NEEDS_ROW: a ticked row without a row it needs, when the card could
 * not check it first (plan P2-3). Retrying fails the same way, so the notice
 * says which row to tick too: a size's removal or rename, or a stock item's
 * unit change, by its name, read from the proposal (plan P4-1, P5-3); else
 * (a new category, group or stock item, or that row unreadable) by its key's
 * family, naming none. The failure to retry for a family without a notice.
 */
async function needsRowRefused(
  run: IActionRun,
  proposalId: string,
  needs: string
): Promise<IGhCardActionResult> {
  if (needsSizeRow(needs) || needsStockUnitRow(needs)) {
    const view = await readProposal(run, proposalId);
    const required = view?.rows.find((row) => row.key === needs);
    const named = required ? requiredRowResult(run, required) : null;
    if (named) return named;
  }
  const key = needsRowKeyOf(needs);
  return key ? needsRowResult(run, key) : applyFailed(run);
}

/** How the notice of an apply that would break something names it. */
interface IBreaksNotice {
  /** i18n key of the notice naming the subject, and its placeholder. */
  key: string;
  param: string;
  /** The same notice without the subject's name. */
  anyKey: string;
  /** The row changes such a subject (its `itemName` is the subject's name). */
  isSubjectRow(row: IAssistantRow): boolean;
}

/**
 * The ticked rows of an option group, without its other rows, would leave
 * it broken (plan P3-3: the Action API checks the approved rows of each
 * group with the editor's rules and answers 422 `NO_ACTIVE_CHOICE` /
 * `RULES_INVALID` with the key of the group's first ticked row).
 */
const GROUP_BREAKS_NOTICE: IBreaksNotice = {
  key: 'assistant.proposal.groupBreaks',
  param: 'group',
  anyKey: 'assistant.proposal.groupBreaksAny',
  isSubjectRow: (row) => changedGroupId(row) !== null,
};

/**
 * The ticked variant rows of a product, without its other variant rows,
 * would leave it with no variant on sale or a switch beside another variant
 * change (plan P4-1: 422 `NO_ACTIVE_SIZE` / `SIZES_CONFLICT` with the key
 * of the product's first ticked variant row, whose `itemName` is the
 * product).
 */
const SIZE_BREAKS_NOTICE: IBreaksNotice = {
  key: 'assistant.proposal.sizeBreaks',
  param: 'name',
  anyKey: 'assistant.proposal.sizeBreaksAny',
  isSubjectRow: isSizeRow,
};

/**
 * The ticked new variants of a product, without the removals that made
 * room, would give it more than 20 variants (plan P4-1: 422 `SIZE_LIMIT`
 * with the key of the product's first ticked variant row).
 */
const SIZE_LIMIT_NOTICE: IBreaksNotice = {
  key: 'assistant.proposal.sizeLimit',
  param: 'name',
  anyKey: 'assistant.proposal.sizeLimitAny',
  isSubjectRow: isSizeRow,
};

/**
 * An apply whose ticked rows would break what they change. Retrying fails
 * the same way, so the notice names that subject (the row at `key`, read
 * from the proposal) and asks to tick its other changes too; the card stays
 * and the proposal still waits. Without the subject's name (no key, the
 * proposal unreadable, a row it does not list or of another kind), the
 * same notice without it.
 */
async function breaksResult(
  run: IActionRun,
  proposalId: string,
  key: string | undefined,
  notice: IBreaksNotice
): Promise<IGhCardActionResult> {
  const view = key ? await readProposal(run, proposalId) : null;
  const row = view?.rows.find((candidate) => candidate.key === key);
  const subject = row && notice.isSubjectRow(row) ? row.itemName : '';
  return {
    notice: subject
      ? fitText(
          run.fmt,
          notice.key,
          { [notice.param]: subject },
          notice.param,
          NOTICE_MAX
        )
      : clampText(run.fmt.t(notice.anyKey), NOTICE_MAX),
  };
}

/** A failed apply: stale, a failure to retry, or whatever the proposal says now. */
async function applyRefused(
  error: unknown,
  run: IActionRun,
  proposalId: string
): Promise<IGhCardActionResult> {
  const body = readAssistantError(error);
  if (!body) {
    if (!isUncertain(error)) throw error;
    // The apply may have gone through: the proposal says whether it did.
    const view = await readProposal(run, proposalId);
    if (!view) return applyFailed(run);
    if (view.status === 'proposed')
      return hasRowsToApply(view)
        ? applyFailed(run)
        : nothingLeftResult(run, proposalId);
    return closedProposalResult(run, proposalId, view.status, { view });
  }
  switch (body.code) {
    case 'ASSISTANT_STALE':
      return staleResult(run, body.details);
    case 'ASSISTANT_NOT_FOUND':
      // It no longer exists: it never waits again.
      run.host.deps.assistantStore.clearPending(proposalId);
      return applyFailed(run);
    case 'ASSISTANT_INVALID_OP': {
      const reason = body.details?.reason;
      // Ticked rows that would break their option group (plan P3-3).
      if (reason && GROUP_BREAKS_REASONS.includes(reason))
        return breaksResult(
          run,
          proposalId,
          body.details?.key,
          GROUP_BREAKS_NOTICE
        );
      // Ticked rows that would break their product's variants (plan P4-1):
      // too many variants says so; the other reasons, none valid left.
      if (reason && SIZE_BREAKS_REASONS.includes(reason))
        return breaksResult(
          run,
          proposalId,
          body.details?.key,
          reason === 'SIZE_LIMIT' ? SIZE_LIMIT_NOTICE : SIZE_BREAKS_NOTICE
        );
      // A ticked row without a row it needs, when the card could not check it
      // first (plan P2-3): retrying fails the same way, so say which to tick.
      const needs = reason === 'NEEDS_ROW' ? body.details?.needs : undefined;
      return needs ? needsRowRefused(run, proposalId, needs) : applyFailed(run);
    }
    case 'ASSISTANT_TOO_MANY_ROWS':
      return applyFailed(run);
    default:
      return proposalRefused(error, run, proposalId);
  }
}

/**
 * A ticked row that needs a row the member left unticked (plan P2-3): the
 * notice to tick that one too, instead of an apply the Action API refuses
 * (422 NEEDS_ROW). The proposal is read only when the card lists rows other
 * rows need (`required`) and one of them is unticked. Null when every ticked
 * row has what it needs, or when that cannot be told here (the proposal
 * cannot be read, no longer waits, or is not the view the member saw): the
 * Action API decides then, and its NEEDS_ROW gets the same notice.
 */
async function needsRowNotice(
  run: IActionRun,
  proposalId: string,
  viewHash: string,
  selected: readonly string[]
): Promise<IGhCardActionResult | null> {
  const required = payloadRequiredKeys(run.payload);
  if (required.every((key) => selected.includes(key))) return null;
  const view = await readProposal(run, proposalId);
  if (!view || view.status !== 'proposed' || view.viewHash !== viewHash)
    return null;
  const unmet = unmetRequirement(view.rows, selected);
  return unmet ? requiredRowResult(run, unmet) : null;
}

/**
 * «Aplicar» and the review's confirm: applies the rows the member left
 * ticked (`selectedRowKeys`) of the view they saw (`viewHash`); a ticked row
 * without a row it needs is not sent (the card stays, with why under it).
 */
export async function applyProposalAction(
  run: IActionRun
): Promise<IGhCardActionResult> {
  const proposalId = requirePayloadString(run, 'proposalId');
  const viewHash = requirePayloadString(run, 'viewHash');
  const selected = run.event.selectedRowKeys ?? [];
  const needsRow = await needsRowNotice(run, proposalId, viewHash, selected);
  if (needsRow) return needsRow;
  let result: IAssistantApplyResult;
  try {
    result = await AssistantService.apply(run.scope.restaurantId, proposalId, {
      schemaVersion: ASSISTANT_SCHEMA_VERSION,
      keys: [...selected],
      viewHash,
    });
  } catch (error) {
    return applyRefused(error, run, proposalId);
  }
  const view = needsRows(result, payloadRows(run))
    ? await readProposal(run, proposalId)
    : null;
  return appliedResult(run, result, view);
}

/** «Descartar». */
export async function discardProposalAction(
  run: IActionRun
): Promise<IGhCardActionResult> {
  const proposalId = requirePayloadString(run, 'proposalId');
  let response: IAssistantDiscardResponse;
  try {
    response = await AssistantService.discard(
      run.scope.restaurantId,
      proposalId
    );
  } catch (error) {
    return proposalRefused(error, run, proposalId);
  }
  run.host.deps.assistantStore.clearPending(proposalId);
  Sentry.addBreadcrumb({
    category: 'assistant',
    message: 'discard',
    data: { proposalId, status: response.status },
  });
  return {
    card:
      response.status === 'superseded'
        ? supersededCard(run.event.cardId, run.fmt)
        : discardedCard(run.event.cardId, run.fmt),
  };
}

/**
 * The proposal a stale apply saved, with the rows that changed marked: its
 * diff card goes through the review, those rows unticked. Null when it no
 * longer waits or has nothing to apply (a refresh then says what it is).
 */
async function savedStaleProposal(
  run: IActionRun,
  proposalId: string
): Promise<IGhCardActionResult | null> {
  const view = await readProposal(run, proposalId);
  if (!view || view.status !== 'proposed' || !hasRowsToApply(view)) return null;
  run.host.deps.assistantStore.markPending(
    view.id,
    view.rowCount,
    view.expiresAt,
    null,
    buildScopeKey(run.scope)
  );
  return diffCardResult(run, view);
}

/**
 * «Revisar de nuevo» / «Preparar de nuevo»: the proposal rendered again
 * against the live menu, as a new diff card (30 more minutes). After a
 * stale apply, the saved proposal instead: rendering it again would lose
 * which rows changed.
 */
export async function refreshProposalAction(
  run: IActionRun
): Promise<IGhCardActionResult> {
  const proposalId = requirePayloadString(run, 'proposalId');
  if (run.payload.stale === true) {
    const saved = await savedStaleProposal(run, proposalId);
    if (saved) return saved;
  }
  let view: IAssistantProposalView;
  try {
    view = await AssistantService.refresh(run.scope.restaurantId, proposalId);
  } catch (error) {
    return proposalRefused(error, run, proposalId);
  }
  Sentry.addBreadcrumb({
    category: 'assistant',
    message: 'refresh',
    data: { proposalId, rows: view.rowCount },
  });
  if (!hasRowsToApply(view)) return nothingLeftResult(run, view.id);
  run.host.deps.assistantStore.markPending(
    view.id,
    view.rowCount,
    view.expiresAt,
    null,
    buildScopeKey(run.scope)
  );
  return diffCardResult(run, view);
}
